// HPV & oral/oropharyngeal cancer — pre→post educational-intervention KAP instrument.
// IMPORTANT: this file ships to the browser, so it contains NO correct answers
// for knowledge items and NO reverse-coding flags for attitude items.
// Scoring and reverse-coding are done server-side (supabase/functions/score).
import { baseDemographics, TF, mc, same, NOT_YET_SEEING } from './common';

// ---- Section A: demographics (shared, plus one topic item) ----
const demographics = [
  ...baseDemographics,
  { key: 'A4', field: 'prior_training', type: 'single', required: true,
    stem: 'Have you received any formal teaching on HPV and its link to oral/oropharyngeal cancer so far?',
    options: same('Yes', 'No', 'Not sure') },
];

// ---- Section B: knowledge ----
const knowledge = [
  { key: 'KQ1', type: 'single', options: TF,
    stem: 'Human papillomavirus (HPV) is a recognised cause of a rising share of oropharyngeal (tonsil and base-of-tongue) cancers.' },
  { key: 'KQ2', type: 'single', options: TF,
    stem: 'HPV is the main cause of most typical oral-cavity cancers (for example, cancer of the tongue margin, floor of mouth or cheek).' },
  { key: 'KQ3', type: 'single',
    stem: 'Which HPV genotype is most strongly associated with HPV-positive oropharyngeal cancer?',
    options: mc('HPV-6', 'HPV-11', 'HPV-16', 'HPV-42') },
  { key: 'KQ4', type: 'single',
    stem: 'Which factor is the principal cause of MOST oral-cavity (not oropharyngeal) squamous cell carcinoma?',
    options: mc('HPV infection', 'Tobacco, areca/betel nut and alcohol use', 'Poor toothbrushing alone', 'Hot beverages alone') },
  { key: 'KQ5', type: 'single', options: TF,
    stem: 'Oral HPV infection can be transmitted through oral sexual contact.' },
  { key: 'KQ6', type: 'single', options: TF,
    stem: 'A safe and effective vaccine against high-risk HPV types is available.' },
  { key: 'KQ7', type: 'single',
    stem: 'According to current mainstream guidance, routine HPV vaccination is recommended starting at approximately:',
    options: mc('Ages 9–12 years', 'Age 18 years only', 'Age 30 years only', 'Only after first symptoms appear') },
  { key: 'KQ8', type: 'single', options: TF,
    stem: 'HPV vaccination is recommended for both girls/women and boys/men (gender-neutral), with catch-up generally offered up to about age 26. (ACIP advises shared clinical decision-making for ages 27–45.)' },
  { key: 'KQ9', type: 'single',
    stem: 'Which HPV vaccine formulation protects against the greatest number of HPV types?',
    options: mc('Bivalent (2 types)', 'Quadrivalent (4 types)', 'Nine-valent / 9-valent (9 types)', 'A live attenuated vaccine') },
  { key: 'KQ10', type: 'single',
    stem: 'Which set of features is most suggestive of an oropharyngeal cancer?',
    options: mc('Persistent sore throat, difficulty swallowing and a painless neck (lymph node) mass', 'A transient cold that clears in a week', 'Bleeding gums that resolve with scaling', 'Generalised tooth sensitivity to cold') },
  { key: 'KQ11', type: 'single', options: TF,
    stem: 'Compared with HPV-negative tumours, HPV-positive oropharyngeal cancer generally carries a better prognosis (better treatment response and survival).' },
  { key: 'KQ12', type: 'single', options: TF,
    stem: 'The dental team has a role in examining the oropharynx, educating patients about HPV, and advocating for HPV vaccination.' },
];

// ---- Section C: attitude (5-point Likert) ----
// Reverse-coded (negatively-worded) items: AQ3, AQ5, AQ7 (handled server-side in keys.ts).
const attitude = [
  { key: 'AQ1', type: 'likert',
    stem: 'HPV-related oropharyngeal cancer is a serious problem that is relevant to my future dental practice.' },
  { key: 'AQ2', type: 'likert',
    stem: 'Dental professionals have an important role in the prevention and early detection of HPV-related oral/oropharyngeal cancer.' },
  { key: 'AQ3', type: 'likert',
    stem: 'Discussing HPV with patients is too awkward or sensitive to be part of routine dental care.' },
  { key: 'AQ4', type: 'likert',
    stem: 'HPV vaccination is a safe and effective way to reduce the future burden of HPV-related cancers.' },
  { key: 'AQ5', type: 'likert',
    stem: 'Counselling patients about HPV vaccination is the responsibility of physicians, not the dental team.' },
  { key: 'AQ6', type: 'likert',
    stem: 'Education on HPV and oral/oropharyngeal cancer should be a formal part of the dental curriculum.' },
  { key: 'AQ7', type: 'likert',
    stem: 'Routinely examining the oropharynx adds little value to a dental check-up.' },
  { key: 'AQ8', type: 'likert',
    stem: 'I would welcome additional training on HPV, vaccination counselling and oropharyngeal cancer screening.' },
];

// ---- Section C2: self-efficacy / confidence (5-point Likert; max 25) ----
const selfEfficacy = [
  { key: 'SE1', type: 'likert',
    stem: 'I feel confident discussing HPV and its link to oral/oropharyngeal cancer with patients.' },
  { key: 'SE2', type: 'likert',
    stem: 'I feel confident advising eligible patients (or their parents) about HPV vaccination.' },
  { key: 'SE3', type: 'likert',
    stem: 'I feel confident examining the oropharynx and palpating the neck for cervical lymph nodes.' },
  { key: 'SE4', type: 'likert',
    stem: 'I feel confident recognising features suspicious of oropharyngeal cancer and referring a patient promptly.' },
  { key: 'SE5', type: 'likert',
    stem: 'I feel confident taking a risk history relevant to oral/oropharyngeal cancer (tobacco, areca, alcohol and, where appropriate, sexual-health risk factors).' },
];

// ---- Section D: practice (v2.0) ----
//  • PQ1–PQ7 — self-reported behaviour (descriptive, NOT scored).
//  • V1–V3   — applied-knowledge vignettes, one guideline-appropriate answer each,
//              scored via the practice key (max 3); answerable at any training stage.
//  • IN1–IN2 — behavioural intention (descriptive Likert) for respondents not yet
//              seeing patients.
const practice = [
  { key: 'PQ1', type: 'single',
    stem: 'During clinical work, how often do you examine the oropharynx (tonsils, base of tongue, soft palate) as part of an oral examination?',
    options: [...same('Always', 'Sometimes', 'Never'), NOT_YET_SEEING] },
  { key: 'PQ2', type: 'single',
    stem: 'How often do you palpate the neck for cervical lymph nodes during a head-and-neck examination?',
    options: [...same('Always', 'Sometimes', 'Never'), NOT_YET_SEEING] },
  { key: 'PQ3', type: 'single',
    stem: 'How often do you take a history relevant to oral/oropharyngeal cancer risk (tobacco, areca/betel, alcohol, and where appropriate sexual-health risk factors)?',
    options: [...same('Always', 'Sometimes', 'Never'), NOT_YET_SEEING] },
  { key: 'PQ4', type: 'single',
    stem: 'Have you ever discussed HPV or HPV vaccination with a patient (or a parent/guardian)?',
    options: [...same('Yes', 'No'), NOT_YET_SEEING] },
  { key: 'PQ5', type: 'single',
    stem: 'When you find a sore throat, swelling or ulcer that persists for more than two to three weeks, what do you usually do?',
    options: same('Refer for further evaluation / biopsy', 'Reassure and review later', 'Take no specific action') },
  { key: 'PQ6', type: 'single',
    stem: 'How often do you give patients advice on reducing oral/oropharyngeal cancer risk (e.g., stopping tobacco and areca, limiting alcohol, HPV vaccination)?',
    options: [...same('Always', 'Sometimes', 'Never'), NOT_YET_SEEING] },
  { key: 'PQ7', type: 'single',
    stem: 'Have you ever advised an eligible patient (or parent) to consider HPV vaccination?',
    options: [...same('Yes', 'No'), NOT_YET_SEEING] },
  { key: 'V1', type: 'single',
    stem: 'A 32-year-old non-smoker has a unilateral sore throat, difficulty swallowing and a painless neck lump persisting for over three weeks. The most appropriate action is:',
    options: same(
      'Refer for specialist evaluation / biopsy',
      'Prescribe antibiotics and review',
      'Reassure and review in three months',
      'No action is needed',
    ) },
  { key: 'V2', type: 'single',
    stem: 'A parent of an 11-year-old asks whether the HPV vaccine is relevant to mouth and throat cancers. The best response is:',
    options: same(
      'Yes — recommend HPV vaccination (boys and girls, around ages 9–12); it reduces HPV-related oropharyngeal cancers',
      'It only protects against cervical cancer',
      'It is only for girls',
      'Vaccination is not a dental concern',
    ) },
  { key: 'V3', type: 'single',
    stem: 'A healthy 19-year-old asks whether they are “too old” for the HPV vaccine. The correct advice is:',
    options: same(
      'No — catch-up vaccination is recommended up to about age 26',
      'Yes — the vaccine is only given before age 15',
      'Yes — there is no benefit after the teens',
      'The vaccine is unsafe at this age',
    ) },
  { key: 'IN1', type: 'likert',
    stem: 'When I begin clinical practice, I intend to routinely examine the oropharynx and palpate the neck.' },
  { key: 'IN2', type: 'likert',
    stem: 'I intend to advise eligible patients (or parents) about HPV vaccination when appropriate.' },
];

const education = [
  {
    id: 'hpv-two-cancers',
    title: 'Two cancers, two different causes',
    points: [
      'HPV — especially HPV-16 — is driving a rising share of oropharyngeal cancers (tonsil and base of tongue).',
      'Most typical oral-cavity cancers (tongue margin, floor of mouth, cheek), by contrast, are caused mainly by tobacco, areca/betel nut and alcohol — not HPV.',
      'Oral HPV can be transmitted through oral sexual contact.',
    ],
    tip: '“16 loves the throat”: HPV-16 → oropharynx. Oral-cavity cancers? Blame TAB — Tobacco, Areca, Booze.',
  },
  {
    id: 'hpv-vaccine',
    title: 'The HPV vaccine',
    points: [
      'A safe, effective vaccine against high-risk HPV types is available; routine vaccination is recommended at about ages 9–12.',
      'It is gender-neutral — recommended for boys and girls alike — with catch-up generally offered up to about age 26.',
      'The 9-valent vaccine covers the most HPV types; quadrivalent vaccines (e.g. Gardasil and India’s Cervavac) are what many national programmes use — including India’s national HPV programme.',
    ],
    tip: 'Triple-9 to remember: vaccination can start at age 9, the 9-valent covers 9 types, and it’s for all sexes (catch-up to about 26).',
  },
  {
    id: 'hpv-recognise',
    title: 'Recognise and refer',
    points: [
      'Suspect oropharyngeal cancer with a persistent sore throat, difficulty swallowing, or a painless neck (lymph-node) mass — refer any sore throat, ulcer or swelling that persists beyond two to three weeks.',
      'HPV-positive oropharyngeal cancers generally respond better to treatment and carry a better prognosis than HPV-negative tumours.',
    ],
    tip: 'The 2–3 week rule: a sore throat or neck lump that outstays a cold gets referred. A painless neck lump is the red flag.',
  },
  {
    id: 'hpv-dental-role',
    title: 'The dental team’s role',
    points: [
      'Examine the oropharynx, palpate the neck, and take a risk history (tobacco, areca, alcohol; where appropriate, sexual-health factors).',
      'Educate patients about HPV and advocate for vaccination — the dental team has a genuine role in prevention.',
    ],
    tip: 'The dentist’s HPV checklist: Look · Palpate · Educate · Advocate.',
  },
];

export default {
  slug: 'hpv',
  code: 'KS02',
  title: 'Impact of a Targeted Educational Module on Knowledge, Attitude, and Practice Regarding Human Papillomavirus (HPV) and Its Role in Oral and Oropharyngeal Cancer Among Dental Students and Interns: A Pre–Post Interventional Study',
  shortTitle: 'HPV & Oral Cancer',
  studyTitle: 'KAP on HPV & Oral/Oropharyngeal Cancer',
  topic: 'Oral oncology & prevention',
  emoji: '🧬',
  accent: '#7C5CFF',
  estMinutes: 12,
  mode: 'pre-post',
  status: 'live',
  version: '2.0',
  practiceMax: 3, // scored practice = the three applied vignettes (V1–V3); PQ*/IN* are descriptive
  blurb: 'HPV as a cause of oropharyngeal cancer, vaccination attitudes, and counselling practice — with a built-in learning module that measures what changes.',
  sections: { demographics, knowledge, attitude, selfEfficacy, practice },
  education,
  educationTitle: 'HPV and oral/oropharyngeal cancer: the essentials',
  educationBlurb: 'A two-minute refresher based on current CDC/ACIP and WHO guidance. Read through, then continue to the short follow-up.',
};
